import userEventService from './user-event-service';
import { isNumericOutOfRange, isDateOutOfRange } from '../utils/broker-verification-utils';

const EVENT_CATEGORY = 'Voucher Review';
const EVENT_ACTION = 'broker_verification_field_edited';
const EVENT_SOURCE = 'review_before_you_submit_panel';
const MAX_SERIALIZED_LENGTH = 1000;

const isEmptyValue = value => (
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
  || (Array.isArray(value) && value.length === 0)
);

// Collapses every "blank" representation to null so `previous_value_empty` is reliable.
const normalizeValue = (value) => {
  if (isEmptyValue(value)) return null;
  if (value instanceof Date) return value.toISOString();
  return value;
};

const isNumericString = value => typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim());
const toBooleanLike = (value) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return value;
};

// Numbers are only compared numerically against a real number (a backend Decimal string "42.50"
// vs the 42.5 a control commits). Two strings always compare as text, so ZIP "08540" and "8540"
// stay different.
const valuesAreEqual = (a, b) => {
  const left = normalizeValue(a);
  const right = normalizeValue(b);
  if (left === null || right === null) return left === right;
  if (typeof left === 'boolean' || typeof right === 'boolean') return toBooleanLike(left) === toBooleanLike(right);
  if (typeof left === 'number' && (typeof right === 'number' || isNumericString(right))) return left === Number(right);
  if (typeof right === 'number' && isNumericString(left)) return right === Number(left);
  if (typeof left === 'string' && typeof right === 'string') return left.trim() === right.trim();
  if (typeof left === 'object' && typeof right === 'object') return JSON.stringify(left) === JSON.stringify(right);
  return false;
};

const rangeStatus = (value, bounds) => {
  if (!bounds || isEmptyValue(value)) return 'not_applicable';
  return isNumericOutOfRange(value, bounds) || isDateOutOfRange(value, bounds) ? 'out_of_range' : 'in_range';
};

// Caps oversized values (long text, large arrays) so one event never becomes a huge POST.
const capValue = (value) => {
  if (value === null || typeof value !== 'object') {
    if (typeof value === 'string' && value.length > MAX_SERIALIZED_LENGTH) {
      return { value: value.slice(0, MAX_SERIALIZED_LENGTH), truncated: true };
    }
    return { value, truncated: false };
  }
  const serialized = JSON.stringify(value);
  if (serialized.length > MAX_SERIALIZED_LENGTH) {
    return { value: serialized.slice(0, MAX_SERIALIZED_LENGTH), truncated: true };
  }
  return { value, truncated: false };
};

// user_id/timestamp come from user-event-service (GET /user is the real, non-proxied user).
// Never throws: tracking must not disrupt the user's workflow.
const trackFieldEdit = ({
  dealId, voucherId, dealType, isEarlyInvoice, actingAsUserId,
  fieldKey, fieldLabel, section,
  previousValue, currentValue, previousLabel, currentLabel,
  bounds,
  entityType, entityId, spaceIndex, partyType, rowType,
}) => {
  try {
    const previous = capValue(normalizeValue(previousValue));
    const current = capValue(normalizeValue(currentValue));

    userEventService.trackEvent({
      eventCategory: EVENT_CATEGORY,
      eventAction: EVENT_ACTION,
      eventLabel: fieldKey,
      eventData: {
        deal_id: dealId,
        voucher_id: voucherId,
        deal_type: dealType,
        is_early_invoice: !!isEarlyInvoice,
        source: EVENT_SOURCE,
        section: section || undefined,
        field_key: fieldKey,
        field_label: fieldLabel,
        entity_type: entityType || undefined,
        entity_id: entityId ?? undefined,
        space_index: spaceIndex ?? undefined,
        party_type: partyType || undefined,
        row_type: rowType || undefined,
        previous_value: previous.value,
        current_value: current.value,
        previous_value_label: previousLabel ?? undefined,
        current_value_label: currentLabel ?? undefined,
        previous_value_empty: previous.value === null,
        value_truncated: (previous.truncated || current.truncated) || undefined,
        previous_range_status: bounds ? rangeStatus(previousValue, bounds) : undefined,
        current_range_status: bounds ? rangeStatus(currentValue, bounds) : undefined,
        acting_as_user_id: actingAsUserId ?? undefined,
      },
    });
  } catch (error) {
    console.error('Failed to track broker verification field edit');
  }
};

export {
  EVENT_CATEGORY, EVENT_ACTION, EVENT_SOURCE, normalizeValue, valuesAreEqual, rangeStatus, trackFieldEdit,
};

export default { trackFieldEdit };
